import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { usePrevious, useToaster } from '../../../hooks';
import {
  AuthStore,
  RegistrationStore,
  updateAuth,
  updateAuthIndex,
} from '../../../context';
import { Button } from '../../index';
import { isEmpty } from '../../../util/object.util';
import { parse } from '../../../util/api.util';
import { MerchantSwitcherModal } from '../MerchantSwitcherModal';
import { cloneMerchant } from '../CloneMerchantModal/CloneMerchantModal.util';
import { history } from '../../../history';
import { CloneMerchantModal } from '../CloneMerchantModal';
import { Container } from './Container';
import { Panel } from './Panel';
import { PathMemory } from '../PathMemory';
import { coreapi } from '../../../api';

export const MerchantSwitcher: FunctionComponent = () => {
  const {
    state: { status: authStatus, auth },
    dispatch: authDispatch,
  } = useContext(AuthStore);
  const {
    state: { registration },
  } = useContext(RegistrationStore);

  const isPendingReSignature =
    registration?.signature_status === 'PENDING RE-SIGNATURE';

  const { search } = useLocation();

  const [pathMemoryState, setPathMemoryState] = useState<'initial' | 'ready'>(
    'initial',
  );

  const [hasSelectedMerchant, setHasSelectedMerchant] = useState(false);

  type ModalState = 'closed' | 'switcher' | 'clone';
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedAuthIndex, setSelectedAuthIndex] = useState<number>(0);
  const { toast, toaster } = useToaster();

  // Keep local selected index in sync with auth context (first load / changes)
  useEffect(() => {
    if (auth?.index != null) {
      setSelectedAuthIndex(auth.index);
    }
  }, [auth?.index]);

  const authData = auth && auth.data;
  const authMerchantStatus = auth?.merchant?.status;
  const authMerchantId = auth?.merchant?.id;
  const prevAuthStatus = usePrevious(authStatus);

  useEffect(() => {
    if (authStatus === 'authed.fresh' && prevAuthStatus !== 'authed.fresh') {
      // once we're finished fetching the initial auth,
      // or if the user just logged in,
      // if this user has more than one auth datum
      // (which means they have more than one merchant)
      // OR if the only merchant they have is ACTIVE
      // go ahead and immediately present the merchant switcher
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      const showMerchantSwitcher =
        authData?.length! > 1 || authMerchantStatus === 'ACTIVE';

      if (showMerchantSwitcher) {
        setModalState('switcher');
      } else {
        // if we do not have to show the merchant switcher
        // then pretend they have just selected the current merchant
        setHasSelectedMerchant(true);
      }
    }
  }, [authStatus, prevAuthStatus, authData, authMerchantStatus]);

  useEffect(() => {
    let script: HTMLScriptElement;

    if (hasSelectedMerchant) {
      // once they have selected a merchant
      // we are ok to start memorizing the pathname
      setPathMemoryState('ready');

      if (authMerchantId) {
        /**
         * Q: What is this tags.js script?
         * A: The tags.js script is a Forte script, created by "ThreatMetrix" (https://risk.lexisnexis.com/products/threatmetrix)
         *    and is used to mitigate risk for underwriting. We provide a unique id to the script at the time of signup and then
         *    provide the same ID when creating the Forte application (provisoning the merchant) to tie the two entities (browser data and application) together.
         *
         * Q: Why are we setting `window.tmx_profiling_started = false`?
         * A: The tags.js script expects to be loaded one time with a single `session_id`. Once it's loaded, it sets `window.tmx_profiling_started = true`.
         *    If we try to remove the script and reload it with a different `session_id`, it checks `window.tmx_profiling_started` and, if it's true,
         *    throws an error about the script having been loaded once already. However, since we *need* to change the `session_id` every time the user
         *    select a different merchant, the solution is to set `window.tmx_profiling_started = false` any time we need to reload the script.
         *
         */

        // @ts-ignore
        window.tmx_profiling_started = false;

        script = document.createElement('script');
        script.src = `https://img3.forte.net/fp/tags.js?org_id=xdzpgyj7&pageid=1&session_id=${authMerchantId}`;
        script.async = true;
        document.body.appendChild(script);
      }
    }

    return () => {
      if (script) document.body.removeChild(script);
    };
  }, [hasSelectedMerchant, authMerchantId]);

  useEffect(() => {
    if (search && search.includes('ms')) {
      try {
        const { modalState } = JSON.parse(atob(parse(search).ms));
        if (
          modalState &&
          ['switcher', 'clone', 'closed'].includes(modalState)
        ) {
          setModalState(modalState);
        } else {
          setModalState('switcher');
        }
      } catch (e) {
        console.error('could not parse merchant switcher modal options');
        setModalState('switcher');
      }
    }
  }, [search]);

  useEffect(() => {
    if (isPendingReSignature) {
      /**
       * If the registration is waiting for a resignature, then prevent the merchant switcher from reopening
       * since we want to navigate directly to the review step and open the signature modal
       * and we should avoid interrupting the user with the merchant switcher modal
       */
      setModalState('closed');
    }
  }, [isPendingReSignature]);

  if (isEmpty(auth) || !auth.merchant || !auth.user) return null;

  return (
    <>
      {pathMemoryState === 'ready' && <PathMemory />}
      {modalState === 'switcher' && (
        <MerchantSwitcherModal
          isOpen
          onRequestClone={idx => {
            setSelectedAuthIndex(idx);
            setModalState('clone');
          }}
          onClose={() => {
            setModalState('closed');
            setHasSelectedMerchant(true);
          }}
        />
      )}
      {modalState === 'clone' && auth?.data?.[selectedAuthIndex]?.merchant && (
        <CloneMerchantModal
          isOpen
          merchant={auth.data[selectedAuthIndex].merchant!}
          onClose={() => setModalState('switcher')}
          onClone={async (companyName, selectedSections) => {
            const token = auth?.token;
            const baseMerchant = auth?.data?.[selectedAuthIndex]?.merchant;
            if (!token || !baseMerchant) {
              toaster(
                toast.error(
                  'Missing authentication context to clone merchant.',
                  'Clone Failed',
                ),
              );
              throw new Error('Missing auth context');
            }

            // Capture existing merchant ids so we can detect the newly added merchant
            const previousMerchantIds = (auth?.data || []).map(
              d => d.merchant?.id,
            );

            try {
              await cloneMerchant(
                token,
                baseMerchant,
                companyName,
                selectedSections,
              );

              const newAuthData = await coreapi.get(token, '/self/list');

              // Determine the index of the newly cloned merchant (fallback to 0 if not found)
              const newIndex = (() => {
                const idx = newAuthData.findIndex(
                  d =>
                    d.merchant && !previousMerchantIds.includes(d.merchant.id),
                );
                return idx === -1 ? 0 : idx;
              })();

              authDispatch(updateAuth(newAuthData));
              authDispatch(updateAuthIndex(newIndex));
              setSelectedAuthIndex(newIndex);

              toaster(
                toast.success(
                  `${companyName} has been created.`,
                  `Clone Successful`,
                ),
              );
              setHasSelectedMerchant(true);
              setModalState('closed');
              history.push('/welcome');
            } catch (e) {
              toaster(
                toast.error(
                  `${companyName} failed to create. Please try again.`,
                  'Clone Failed',
                ),
              );
              // Re-throw so child modal keeps its error handling path
              throw e;
            }
          }}
        />
      )}
      <Container
        data-testid="merchant-info-pane"
        id="merchant-info-pane"
        onClick={() => modalState !== 'clone' && setModalState('switcher')}
      >
        <Panel className="-mobile-hide">
          <span>{auth.merchant.company_name}</span>
          <span>{auth.user.email}</span>
        </Panel>
        <Button
          icon="fas fa-exchange-alt"
          data-testid="merchant-switcher-button"
          id="merchant-switcher-button"
          motif="white"
          variant="outline"
          size="tiny"
        />
      </Container>
    </>
  );
};
